import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  UserCircle, Pencil, Sparkles, RefreshCw, Check, Leaf, Dumbbell,
  Shield, Smile, Home, ArrowLeft, ArrowRight, ChefHat
} from "lucide-react";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── V2 Profile Setup (4 steps) ──
const ProfileSetup = ({ memberId, onComplete, initialData }) => {
  const [step, setStep] = useState(1);
  const [dietary, setDietary] = useState(initialData?.dietary_type || null);
  const [ageGroup, setAgeGroup] = useState(initialData?.age_group_v2 || null);
  const [foodGoal, setFoodGoal] = useState(initialData?.food_goal || null);
  const [basePreference, setBasePreference] = useState(initialData?.base_preference || null);
  const [dinnerStyle, setDinnerStyle] = useState(initialData?.dinner_style || null);
  const [saving, setSaving] = useState(false);

  const dietOptions = [
    { value: "VEGAN", label: "Vegan", desc: "No dairy, no animal products" },
    { value: "VEGETARIAN", label: "Vegetarian", desc: "Pure veg, the way I like it" },
    { value: "EGGETARIAN", label: "Egg lover", desc: "Veg + eggs work for me" },
    { value: "NON_VEG", label: "Non-Veg", desc: "I enjoy everything" },
  ];

  const ageOptions = [
    { value: "under_25", label: "Under 25" },
    { value: "25_35", label: "25 - 35" },
    { value: "35_45", label: "35 - 45" },
    { value: "45_plus", label: "45+" },
  ];

  const foodGoalOptions = [
    { value: "stay_light", label: "Stay light & healthy most days", icon: Leaf },
    { value: "balanced_homestyle", label: "Eat balanced home-style meals", icon: Home },
    { value: "mix_healthy_fun", label: "Mix of healthy + fun meals", icon: Smile },
    { value: "fitness_focused", label: "Focus on fitness (high protein)", icon: Dumbbell },
    { value: "watch_sugar", label: "Watch sugar / manage health", icon: Shield },
  ];

  const baseOptions = [
    { value: "roti", label: "Roti person", desc: "Paratha, phulka, roti is life" },
    { value: "rice", label: "Rice person", desc: "Chawal, pulao, biryani — always rice" },
    { value: "balanced", label: "Balanced", desc: "50/50 — I enjoy both equally" },
  ];

  const dinnerOptions = [
    { value: "light", label: "Mostly light dinners", desc: "Khichdi, chilla, soup, simple meals" },
    { value: "balanced", label: "Mostly balanced dinners", desc: "Dal, roti, sabzi — regular home-style" },
    { value: "balanced_indulge", label: "Balanced, but indulge sometimes", desc: "Home-style most days, heavier meals once in a while" },
    { value: "variety_heavy", label: "I enjoy variety, including heavier dinners", desc: "Wraps, paneer, pasta, richer meals more often" },
  ];

  const handleNext = () => {
    if (step === 1 && !dietary) { toast.error("Pick one to continue"); return; }
    if (step === 2 && !ageGroup) { toast.error("Pick your age group"); return; }
    if (step === 2 && !foodGoal) { toast.error("Pick your food goal"); return; }
    if (step === 3 && !basePreference) { toast.error("Pick one to continue"); return; }
    if (step < 4) { setStep(step + 1); return; }
    handleSave();
  };

  const handleSave = async () => {
    if (!memberId) { onComplete(); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/member/${memberId}/profile`, {
        dietary_type: dietary,
        age_group_v2: ageGroup,
        food_goal: foodGoal,
        base_preference: basePreference,
        dinner_style: dinnerStyle || "balanced",
      });
      onComplete();
    } catch (err) {
      if (err.name !== 'CanceledError') {
        toast.error("Could not save. Please try again.");
        setSaving(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6" data-testid="profile-setup">
      <div className="w-full max-w-md mb-8">
        <div className="h-1 bg-border/40 rounded-full overflow-hidden">
          <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${(step / 4) * 100}%` }} />
        </div>
        <p className="text-xs text-muted-foreground mt-2 text-right">{step} of 4</p>
      </div>

      {step === 1 && (
        <div className="w-full max-w-md" data-testid="profile-step-1">
          <h2 className="text-2xl font-bold text-center mb-2" style={{ fontFamily: 'serif' }}>I prefer...</h2>
          <p className="text-muted-foreground text-center mb-8 text-sm">Pick what describes you best</p>
          <div className="space-y-3">
            {dietOptions.map(opt => (
              <button key={opt.value} onClick={() => setDietary(opt.value)}
                className={`w-full text-left px-5 py-4 rounded-2xl border-2 transition-all ${dietary === opt.value ? "border-primary bg-primary/5 shadow-sm" : "border-border/60 bg-white hover:border-primary/30"}`}
                data-testid={`diet-option-${opt.value}`}>
                <span className="font-semibold">{opt.label}</span>
                <span className="block text-xs text-muted-foreground mt-0.5">{opt.desc}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="w-full max-w-md" data-testid="profile-step-2">
          <h2 className="text-2xl font-bold text-center mb-2" style={{ fontFamily: 'serif' }}>A little about you</h2>
          <p className="text-muted-foreground text-center mb-6 text-sm">Helps us personalise your meals</p>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Your age group</p>
          <div className="flex gap-2 mb-8">
            {ageOptions.map(opt => (
              <button key={opt.value} onClick={() => setAgeGroup(opt.value)}
                className={`flex-1 py-2.5 rounded-xl border-2 text-sm font-medium transition-all ${ageGroup === opt.value ? "border-primary bg-primary/5" : "border-border/60 bg-white hover:border-primary/30"}`}
                data-testid={`age-option-${opt.value}`}>
                {opt.label}
              </button>
            ))}
          </div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Your food goals</p>
          <div className="space-y-2.5">
            {foodGoalOptions.map(opt => {
              const Icon = opt.icon;
              return (
                <button key={opt.value} onClick={() => setFoodGoal(opt.value)}
                  className={`w-full flex items-center gap-3 text-left px-4 py-3.5 rounded-2xl border-2 transition-all ${foodGoal === opt.value ? "border-primary bg-primary/5 shadow-sm" : "border-border/60 bg-white hover:border-primary/30"}`}
                  data-testid={`goal-option-${opt.value}`}>
                  <Icon size={18} className="text-primary/60 flex-shrink-0" strokeWidth={1.5} />
                  <span className="font-medium text-sm">{opt.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="w-full max-w-md" data-testid="profile-step-3">
          <h2 className="text-2xl font-bold text-center mb-2" style={{ fontFamily: 'serif' }}>What are you?</h2>
          <p className="text-muted-foreground text-center mb-8 text-sm">This helps us pick the right meals for you</p>
          <div className="space-y-3">
            {baseOptions.map(opt => (
              <button key={opt.value} onClick={() => setBasePreference(opt.value)}
                className={`w-full text-left px-5 py-5 rounded-2xl border-2 transition-all ${basePreference === opt.value ? "border-primary bg-primary/5 shadow-sm" : "border-border/60 bg-white hover:border-primary/30"}`}
                data-testid={`base-option-${opt.value}`}>
                <span className="font-semibold">{opt.label}</span>
                <span className="block text-xs text-muted-foreground mt-0.5">{opt.desc}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="w-full max-w-md" data-testid="profile-step-4">
          <h2 className="text-2xl font-bold text-center mb-2" style={{ fontFamily: 'serif' }}>How does your dinner look?</h2>
          <p className="text-muted-foreground text-center mb-8 text-sm">Dinner is where most variety happens</p>
          <div className="space-y-3">
            {dinnerOptions.map(opt => (
              <button key={opt.value} onClick={() => setDinnerStyle(opt.value)}
                className={`w-full text-left px-5 py-4 rounded-2xl border-2 transition-all ${dinnerStyle === opt.value ? "border-primary bg-primary/5 shadow-sm" : "border-border/60 bg-white hover:border-primary/30"}`}
                data-testid={`dinner-option-${opt.value}`}>
                <span className="font-semibold text-sm">{opt.label}</span>
                <span className="block text-xs text-muted-foreground mt-0.5">{opt.desc}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="w-full max-w-md mt-8 flex gap-3">
        {step > 1 && (
          <Button variant="outline" onClick={() => setStep(step - 1)} className="rounded-full px-6 py-5" data-testid="profile-back-btn">
            <ArrowLeft size={18} />
          </Button>
        )}
        <Button onClick={handleNext} disabled={saving}
          className="flex-1 rounded-full py-5 text-base bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20"
          data-testid="profile-next-btn">
          {saving ? "Saving..." : step === 4 ? "Let's go!" : "Continue"}
          {!saving && <ArrowRight size={18} className="ml-2" />}
        </Button>
      </div>
    </div>
  );
};

// Personalizing transition screen
const PersonalizingScreen = ({ onDone }) => {
  useEffect(() => {
    const timer = setTimeout(onDone, 2500);
    return () => clearTimeout(timer);
  }, [onDone]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6">
      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-6 animate-pulse">
        <Sparkles size={28} className="text-primary" />
      </div>
      <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'serif' }}>Personalizing...</h2>
      <p className="text-sm text-muted-foreground">Tuning your recommendations</p>
    </div>
  );
};

// ── Labels for display ──
const dietLabels = { VEGAN: "Vegan", VEGETARIAN: "Vegetarian", EGGETARIAN: "Egg lover", NON_VEG: "Non-Veg" };
const ageLabels = { under_25: "Under 25", "25_35": "25 - 35", "35_45": "35 - 45", "45_plus": "45+" };
const goalLabels = { stay_light: "Stay light & healthy", balanced_homestyle: "Balanced home-style", mix_healthy_fun: "Mix of healthy + fun", fitness_focused: "Fitness focused", watch_sugar: "Watch sugar / manage health" };
const baseLabels = { roti: "Roti person", rice: "Rice person", balanced: "Balanced (50/50)" };
const dinnerLabels = { light: "Mostly light dinners", balanced: "Mostly balanced dinners", balanced_indulge: "Balanced + indulge sometimes", variety_heavy: "Variety including heavier" };

const ProfilePage = () => {
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [showPersonalizing, setShowPersonalizing] = useState(false);
  const [userEmail] = useState(() => localStorage.getItem("userEmail"));
  const [userName] = useState(() => localStorage.getItem("userName"));
  const [memberId] = useState(() => localStorage.getItem("memberId"));

  const fetchMember = useCallback(async (signal) => {
    if (!userEmail) { setLoading(false); return; }
    try {
      const res = await axios.get(`${API}/member/by-email/${encodeURIComponent(userEmail)}`, { signal });
      setMember(res.data.member);
    } catch (err) {
      if (err.name !== 'CanceledError') console.error("Error fetching member:", err);
    } finally { setLoading(false); }
  }, [userEmail]);

  useEffect(() => {
    const controller = new AbortController();
    fetchMember(controller.signal);
    return () => controller.abort();
  }, [fetchMember]);

  const handleEditComplete = () => { setEditing(false); setShowPersonalizing(true); };
  const handlePersonalizingDone = useCallback(() => {
    setShowPersonalizing(false);
    navigate("/", { state: { refreshPicks: true } });
  }, [navigate]);

  if (!userEmail) {
    return <div className="app-container py-16 text-center"><p className="text-muted-foreground">Please log in to view your profile.</p></div>;
  }
  if (editing) {
    return <ProfileSetup memberId={memberId} onComplete={handleEditComplete}
      initialData={member ? {
        dietary_type: member.dietary_type,
        age_group_v2: member.age_group_v2,
        food_goal: member.food_goal,
        base_preference: member.base_preference,
        dinner_style: member.dinner_style,
      } : null} />;
  }
  if (showPersonalizing) return <PersonalizingScreen onDone={handlePersonalizingDone} />;
  if (loading) return <div className="app-container py-16 flex justify-center"><RefreshCw size={24} className="animate-spin text-primary" /></div>;

  const m = member || {};

  return (
    <div className="app-container py-8 sm:py-12 max-w-lg mx-auto px-6" data-testid="profile-page">
      <div className="text-center mb-10">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <UserCircle size={32} className="text-primary" strokeWidth={1.5} />
        </div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'serif' }}>{userName || "Your Profile"}</h1>
        <p className="text-sm text-muted-foreground mt-1">{userEmail}</p>
      </div>

      {m.preferences_completed ? (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-border/40 p-5" data-testid="profile-diet-card">
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">I prefer</p>
            <p className="text-lg font-semibold">{dietLabels[m.dietary_type] || m.dietary_type || "—"}</p>
          </div>

          <div className="bg-white rounded-2xl border border-border/40 p-5" data-testid="profile-age-goal-card">
            <div className="flex gap-6 mb-3">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Age group</p>
                <p className="text-base font-semibold">{ageLabels[m.age_group_v2] || m.age_group_v2 || "—"}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Food goal</p>
            <p className="text-base font-semibold">{goalLabels[m.food_goal] || m.food_goal || "—"}</p>
          </div>

          <div className="bg-white rounded-2xl border border-border/40 p-5" data-testid="profile-base-card">
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Base preference</p>
            <p className="text-lg font-semibold">{baseLabels[m.base_preference] || m.base_preference || "—"}</p>
          </div>

          <div className="bg-white rounded-2xl border border-border/40 p-5" data-testid="profile-dinner-card">
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Dinner style</p>
            <p className="text-base font-semibold">{dinnerLabels[m.dinner_style] || m.dinner_style || "—"}</p>
          </div>

          <Button onClick={() => setEditing(true)} className="w-full rounded-2xl py-5 text-base gap-2 mt-6" variant="outline" data-testid="profile-edit-btn">
            <Pencil size={16} />Edit Preferences
          </Button>
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-muted-foreground mb-4">You haven't set up your preferences yet.</p>
          <Button onClick={() => setEditing(true)} className="rounded-2xl px-8 py-5 text-base gap-2" data-testid="profile-setup-btn">
            <Sparkles size={16} />Set Up Preferences
          </Button>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
export { ProfileSetup, PersonalizingScreen };
